import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import * as React from "react";
import RefreshIcon from "@mui/icons-material/Refresh";
import IconButton from "@mui/material/IconButton";
import LoadingSpinner from "../components/LoadingSpinner";
import Button from '@mui/material/Button';
import { useState, useEffect } from 'react';
import { format, parse } from 'date-format-parse';
import { useMyOrders } from '../hooks/useMyOrders';
import ErrorFallBack from "../components/ErrorFallBack";
import DownloadIcon from "@mui/icons-material/Download";
import { useOrder } from "../hooks/useOrder";
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import { DataGridPremium, GridValueGetterParams, GridRenderCellParams } from '@mui/x-data-grid-premium';
import { styled } from '@mui/material/styles';
import { Storefront } from "../models/StorefrontResponse";
import { Typography } from "@mui/material";
import OrderDetails from "../components/OrderDetails";
import Link from '@mui/material/Link';
import { useNavigate } from "react-router-dom";
import { useUploadErrorLog } from "../hooks/useUploadErrorLog";
import { useLog } from "../hooks/useLogs";
import { useTSGUser } from "../contexts/TSGUserContext";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const window: any; // Needed for Hubspot

interface Props {
  storefront: Storefront
}

const MyOrders = (props: Props) => {
  const navigate = useNavigate();
  const { logs, logInfo, logError } = useLog();
  const [username, setUsername] = useState("")
  const [refreshKey, setRefreshKey] = useState(0);
  const [orderID, setOrderID] = useState("");
  const { order } = useOrder(orderID, true);
  const [openOrderDetails, setOpenOrderDetails] = useState(false);
  const [orderDetailsID, setOrderDetailsID] = useState("");
  const [errorMessage, setErrorMessage] = useState("")
  const { errorLogAcknowledged } = useUploadErrorLog("MyOrders", errorMessage, username, logs)
  const { tsgUser } = useTSGUser();

  useEffect(() => {
    try {
      setUsername(tsgUser.id || "unknown");
    }
    catch(e) {
      logError(`signin error: ${e as string}`)
      navigate("/signin")
    }
  })
  
  useEffect(() => {
    // Log Pageview with Hubspot
    const _hsq = window._hsq = window._hsq || [];
    _hsq.push(['setPath', '/myorders' + location.search]);
    _hsq.push(['trackPageView']);
  }, []);
  
  useEffect(() => {
    //logInfo("download logged")
   }, [order]);

  useEffect(() => {
    if (errorMessage != "") {
      logInfo("errorLogSubmitted: result=" + errorLogAcknowledged + " - '" + errorMessage + "'")
    }
  }, [errorLogAcknowledged]);

  const { orders, loading, ordersError } = useMyOrders(username, refreshKey);

  useEffect(() => {
   setErrorMessage(ordersError)
  }, [ordersError]);

  const columns = [
        { field: 'id', headerName: 'ID', width: 147, renderCell: (params: GridRenderCellParams) => {
      return         <Link
          component="button"
          variant="body2"
          onClick={() => {
            if (params.value != "TSG-20240420-SMPL") {
              setOrderDetailsID(params.value);
              setOpenOrderDetails(true);
            }
          }}
          >
          {params.value}
        </Link>
    } },
    { field: 'orderDate', headerName: 'Date', width: 85, valueGetter: (params: GridValueGetterParams) => {
      return getShortDate(params.value)
    }, },
    { field: 'description', headerName: 'Description', flex: 1, width: 340 },
    { field: 'totalPrice', headerName: 'Amount', width: 100, valueGetter: (params: GridValueGetterParams) => {
      return formatter.format(params.value);
    },},
    { field: 'receiptURL', headerName: 'Receipt', width: 80, renderCell: (params: GridRenderCellParams) => {
      if (params.value == undefined || params.value == "") {
        return <div></div>
      } else {
        return <Button href={`${params.value}`} download={"TSG Receipt"} size="small">
          <DownloadIcon color="primary" />
        </Button>
      }
    },},
    { field: 'fulfillment', headerName: 'Download', width: 80, renderCell: (params: GridRenderCellParams) => {
      if (params.value.downloadURL == "na") {
        return <div></div>
      } else if (params.value.downloadURL != "") {
        return <Button href={`${params.value.downloadURL}`} download={"TSG Order"} size="small" onClick={() => {if (params.id != "TSG-20240420-SMPL") {logDownload(params.row.id)}}}>
          <DownloadIcon color="primary" />
        </Button>
      } else {
        return <div>Pending...</div>
      }
    }, },
  ]

  function getShortDate(dateString: string | undefined) {
    if (dateString != undefined) {
      const dt = parse(dateString, 'YYYY-MM-DDTHH:mm:ss');
      return format(dt, "MM/DD/YY")
    }
  }

  function logDownload(orderID: string) {
    setOrderID(orderID)
  }

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',

    // These options are needed to round to whole numbers if that's what you want.
    //minimumFractionDigits: 0, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
    //maximumFractionDigits: 0, // (causes 2500.99 to be printed as $2,501)
  });

  const handleRefreshClick = () => {
    setRefreshKey(refreshKey+1);
  };

  const StyledGridOverlay = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    '& .ant-empty-img-1': {
      fill: theme.palette.mode === 'light' ? '#aeb8c2' : '#262626',
    },
    '& .ant-empty-img-2': {
      fill: theme.palette.mode === 'light' ? '#f5f5f7' : '#595959',
    },
    '& .ant-empty-img-3': {
      fill: theme.palette.mode === 'light' ? '#dce0e6' : '#434343',
    },
    '& .ant-empty-img-4': {
      fill: theme.palette.mode === 'light' ? '#fff' : '#1c1c1c',
    },
    '& .ant-empty-img-5': {
      fillOpacity: theme.palette.mode === 'light' ? '0.8' : '0.08',
      fill: theme.palette.mode === 'light' ? '#f5f5f5' : '#fff',
    },
  }));

  function CustomNoRowsOverlay() {
    return (
      <StyledGridOverlay>
        <Box width={100}>
          <svg xmlns="http://www.w3.org/2000/svg" version="1.1" x="0px" y="0px" viewBox="0 0 512 640" enableBackground="new 0 0 512 512"><g><g>
            <polygon points="448,384 181.531,384 69.531,128 0,128 0,96 90.469,96 202.469,352 448,352   " fill="#797a7a"/></g>
            <path d="M288,464c0,26.5-21.5,48-48,48s-48-21.5-48-48s21.5-48,48-48S288,437.5,288,464z" fill="#797a7a"/>
            <path d="M448,464c0,26.5-21.5,48-48,48s-48-21.5-48-48s21.5-48,48-48S448,437.5,448,464z" fill="#797a7a"/><g>
              <path d="M400,0c-61.844,0-112,50.156-112,112s50.156,112,112,112s112-50.156,112-112S461.844,0,400,0z M456.563,145.938    l-22.625,22.625L400,134.625l-33.938,33.938l-22.625-22.625L377.375,112l-33.938-33.938l22.625-22.625L400,89.375l33.938-33.938    l22.625,22.625L422.625,112L456.563,145.938z" fill="#797a7a"/></g>
              <path d="M400,256c-62.5,0-115.281-40.25-135.156-96H160l64,160h224l36.938-92.313C461.063,245.25,431.844,256,400,256z" fill="#797a7a"/></g>
          </svg>
        </Box>
        <Box sx={{ mt: 1, color:"#797a7a" }}>No Purchases</Box>
      </StyledGridOverlay>
    );
  }

  const orderDetailsStyle = {
    position: 'absolute',
    top: '40%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 800,
    bgcolor: props.storefront.activeColors.addProductForm.formBackground,
    border: '1px solid #5c5c5e',
    boxShadow: 24,
    p: 1,
  };

  const handleOrderDetailsClose = () => setOpenOrderDetails(false);

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper sx={{ p: 2, display: "flex", flexDirection: "column" }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              spacing={2}
            >

              <Typography variant="h6" sx={{color: props.storefront.activeColors.mainPage.pageText}}>My Purchases</Typography>
              <Stack direction="row">
                <IconButton size="medium" type="button" onClick={() => handleRefreshClick()} sx={{height:40, marginTop:1}}>
                  <RefreshIcon sx={{color:props.storefront.activeColors.mainPage.pageIconBackground}} />
                </IconButton>
              </Stack>
            </Stack>
            <Grid item xs={12}>
              <Paper sx={{ paddingTop: 2, display: "flex", flexDirection: "column" }}>
                <Grid
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "flex-end",
                  }}
                  item
                  xs={12}
                >
                </Grid>
                <Grid height={500}>
                  {loading
                    ? <LoadingSpinner title="Loading Orders..." textColor="#0352fc" />
                    : ordersError
                    ? <ErrorFallBack message={ordersError}/>
                    :
                    <DataGridPremium
                    rows={orders}
                    columns={columns}
                    checkboxSelection
                    slots={{
                      noRowsOverlay: CustomNoRowsOverlay,
                    }}
                    initialState={{
                      pagination: {
                        paginationModel: { page: 0, pageSize: 10 },
                      },
                      sorting: {
                        sortModel: [{ field: 'id', sort: 'desc' }],
                      },
                    }}
                    pageSizeOptions={[5, 10, 25, 50]}
                    onRowSelectionModelChange={(ids) => {
                      const selectedIDs = new Set(ids);
                      const arrayIDs: string[] = [];
                      selectedIDs.forEach(v => arrayIDs.push(v.toString()));
                    }}
                  />
                    }
                  </Grid>
              </Paper>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
      <Modal
          open={openOrderDetails}
          onClose={handleOrderDetailsClose}
      >
          <Box sx={orderDetailsStyle}>
              <OrderDetails orderID={orderDetailsID} showFullDetails={false} close={() => {setOpenOrderDetails(false)}} />
          </Box>
      </Modal>
    </Container>
  );
};

export default MyOrders;